import type { AuthSessionStore, SessionStoreCommand } from '../types/ports';
import type { User } from '../types/user';

const KEY_PENDING_ERROR = 'auth:pendingError';
const KEY_NEEDS_PHONE = 'auth:needsPhone';
const KEY_SOCIAL_PENDING = 'auth:socialPending';
const KEY_SOCIAL_PROFILE = 'auth:socialProfile';
const KEY_PENDING_USER = 'auth:pendingUser';

export class SessionStorageAdapter implements AuthSessionStore {
  dispatch(command: SessionStoreCommand): void {
    switch (command.type) {
      case 'SET_PENDING_ERROR':
        this.write(KEY_PENDING_ERROR, JSON.stringify(command.error));
        break;
      case 'CLEAR_PENDING_ERROR':
        this.remove(KEY_PENDING_ERROR);
        break;
      case 'SET_NEEDS_PHONE':
        this.write(KEY_NEEDS_PHONE, '1');
        break;
      case 'CLEAR_NEEDS_PHONE':
        this.remove(KEY_NEEDS_PHONE);
        break;
      case 'SET_SOCIAL_PENDING':
        this.writeLocal(KEY_SOCIAL_PENDING, JSON.stringify(command.state));
        break;
      case 'CLEAR_SOCIAL_PENDING':
        this.removeLocal(KEY_SOCIAL_PENDING);
        break;
      case 'SET_SOCIAL_PROFILE':
        this.writeLocal(KEY_SOCIAL_PROFILE, JSON.stringify(command.profile));
        break;
      case 'CLEAR_SOCIAL_PROFILE':
        this.removeLocal(KEY_SOCIAL_PROFILE);
        break;
      case 'SET_PENDING_AUTH':
        this.writeLocal(KEY_PENDING_USER, JSON.stringify(command.user));
        break;
      case 'CLEAR_PENDING_AUTH':
        this.removeLocal(KEY_PENDING_USER);
        break;
      case 'CLEAR_ALL':
        this.remove(KEY_PENDING_ERROR);
        this.remove(KEY_NEEDS_PHONE);
        this.removeLocal(KEY_SOCIAL_PENDING);
        this.removeLocal(KEY_SOCIAL_PROFILE);
        this.removeLocal(KEY_PENDING_USER);
        break;
    }
  }

  getPendingError(): { code?: string; message: string } | null {
    const raw = this.read(KEY_PENDING_ERROR);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as { code?: string; message: string };
    } catch (err) {
      console.warn('[auth] SessionStorageAdapter: failed to parse pending error', err);
      return null;
    }
  }

  getNeedsPhone(): boolean {
    return this.read(KEY_NEEDS_PHONE) === '1';
  }

  getSocialPending(): { provider: string; returnTo: string; phase: string } | null {
    const raw = this.readLocal(KEY_SOCIAL_PENDING);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as { provider: string; returnTo: string; phase: string };
    } catch (err) {
      console.warn('[auth] SessionStorageAdapter: failed to parse social pending state', err);
      return null;
    }
  }

  getSocialProfile(): { firstName: string; lastName: string } | null {
    const raw = this.readLocal(KEY_SOCIAL_PROFILE);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as { firstName: string; lastName: string };
    } catch (err) {
      console.warn('[auth] SessionStorageAdapter: failed to parse social profile', err);
      return null;
    }
  }

  getPendingAuth(): User | null {
    const rawUser = this.readLocal(KEY_PENDING_USER);
    if (!rawUser) return null;
    try {
      return JSON.parse(rawUser) as User;
    } catch (err) {
      console.warn('[auth] SessionStorageAdapter: failed to parse pending auth', err);
      return null;
    }
  }

  // sessionStorage only exists in the browser. On the server (SSR) these are no-ops — without the
  // window guard they throw ReferenceError, which is caught but floods CI logs with warnings.
  private read(key: string): string | null {
    if (typeof window === 'undefined') return null;
    try { return sessionStorage.getItem(key); } catch (err) { console.warn('[auth] SessionStorageAdapter: getItem failed', err); return null; }
  }

  private write(key: string, value: string): void {
    if (typeof window === 'undefined') return;
    try { sessionStorage.setItem(key, value); } catch (err) { console.warn('[auth] SessionStorageAdapter: setItem failed', err); }
  }

  private remove(key: string): void {
    if (typeof window === 'undefined') return;
    try { sessionStorage.removeItem(key); } catch (err) { console.warn('[auth] SessionStorageAdapter: removeItem failed', err); }
  }

  // localStorage is used for keys that must survive cross-origin redirects (e.g. social OAuth flow).
  // iPhone Safari clears sessionStorage when navigating through multiple cross-origin hops,
  // which breaks the Apple/Google/Facebook sign-in callback on mobile.
  private readLocal(key: string): string | null {
    if (typeof window === 'undefined') return null;
    try { return localStorage.getItem(key); } catch (err) { console.warn('[auth] SessionStorageAdapter: localStorage.getItem failed', err); return null; }
  }

  private writeLocal(key: string, value: string): void {
    if (typeof window === 'undefined') return;
    try { localStorage.setItem(key, value); } catch (err) { console.warn('[auth] SessionStorageAdapter: localStorage.setItem failed', err); }
  }

  private removeLocal(key: string): void {
    if (typeof window === 'undefined') return;
    try { localStorage.removeItem(key); } catch (err) { console.warn('[auth] SessionStorageAdapter: localStorage.removeItem failed', err); }
  }
}
